import * as Sentry from '@sentry/browser'
import { reactive, readonly } from 'vue'

import { getConsentCookie, setConsentCookie } from '@/lib/cookieHandling'

const state = reactive({
  choice: [],
})

// Initialize from saved cookie if exists
try {
  const savedChoice = getConsentCookie()
  if (Array.isArray(savedChoice)) {
    state.choice = savedChoice
  }
} catch (err) {
  console.warn('Invalid cookie consent data found', err)
  Sentry.captureException(err, {
    tags: {
      module: 'cookieConsent',
      function: 'initialization',
    },
  })
}

export function useCookieConsent() {
  const accept = (choice) => {
    try {
      if (Array.isArray(choice)) {
        state.choice = choice
        const success = setConsentCookie(choice)
        if (!success) {
          console.warn('Cookie consent: Failed to set cookie, but updated state')
          Sentry.captureMessage('Failed to set cookie consent, but updated state', {
            level: 'warning',
            tags: {
              module: 'cookieConsent',
              function: 'accept',
            },
          })
        }
        return true
      } else {
        console.warn('Cookie consent: Invalid choice format, expected array')
        Sentry.captureMessage('Invalid cookie consent choice format', {
          level: 'warning',
          tags: {
            module: 'cookieConsent',
            function: 'accept',
          },
          extra: {
            choiceType: typeof choice,
          },
        })
        // Apply safe default
        state.choice = ['mandatory']
        setConsentCookie(['mandatory'])
        return false
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          module: 'cookieConsent',
          function: 'accept',
        },
      })
      // Apply safe default
      try {
        state.choice = ['mandatory']
        setConsentCookie(['mandatory'])
      } catch (e) {
        Sentry.captureException(e, {
          tags: {
            module: 'cookieConsent',
            function: 'accept_fallback',
          },
          level: 'fatal',
        })
      }
      return false
    }
  }

  const hasAccepted = () => {
    try {
      return Boolean(state.choice) && !!state.choice.length
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          module: 'cookieConsent',
          function: 'hasAccepted',
        },
      })
      return false
    }
  }

  const hasTrackingConsent = () => {
    try {
      return Array.isArray(state.choice) && state.choice.includes('tracking')
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          module: 'cookieConsent',
          function: 'hasTrackingConsent',
        },
      })
      return false
    }
  }

  return {
    choice: readonly(state.choice),
    accept,
    hasAccepted,
    hasTrackingConsent,
  }
}

// Get initial consent state without creating reactive refs
export function getInitialConsent() {
  try {
    return {
      choice: state.choice,
      hasTrackingConsent: () => Array.isArray(state.choice) && state.choice.includes('tracking'),
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieConsent',
        function: 'getInitialConsent',
      },
    })
    // Return safe defaults
    return {
      choice: ['mandatory'],
      hasTrackingConsent: () => false,
    }
  }
}

// Plugin for backwards compatibility
export const cookieConsentPlugin = {
  install(app) {
    try {
      const cookieConsent = useCookieConsent()
      app.config.globalProperties.$cookieConsent = cookieConsent
      app.provide('cookieConsent', cookieConsent)
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          module: 'cookieConsent',
          function: 'plugin_install',
        },
      })
    }
  },
}
